type LogLevel = "debug" | "info" | "warn" | "error";

type ApiLogContext = {
  method: string;
  url: string;
  payload?: unknown;
};

const SENSITIVE_KEYS = new Set([
  "accessToken",
  "refreshToken",
  "password",
  "sn",
  "newSn",
  "securePayload",
  "input",
  "output",
]);
const MAX_LOG_DEPTH = 6;

function isApiLoggerEnabled(): boolean {
  if (import.meta.env.MODE === "test") return false;
  if (import.meta.env.VITE_ENABLE_API_LOGGER === "false") return false;
  return import.meta.env.DEV || import.meta.env.VITE_ENABLE_API_LOGGER === "true";
}

function sanitize(value: unknown, depth = 0): unknown {
  if (depth > MAX_LOG_DEPTH) return "[MaxDepth]";
  if (value == null) return value;
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => sanitize(item, depth + 1));

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      SENSITIVE_KEYS.has(key) ? "[REDACTED]" : sanitize(item, depth + 1),
    ]),
  );
}

function elapsed(startedAt: number): string {
  return `${Math.round(performance.now() - startedAt)}ms`;
}

export class Logger {
  constructor(private readonly scope: string) {}

  debug(message: string, data?: unknown): void {
    this.write("debug", message, data);
  }

  info(message: string, data?: unknown): void {
    this.write("info", message, data);
  }

  warn(message: string, data?: unknown): void {
    this.write("warn", message, data);
  }

  error(message: string, data?: unknown): void {
    this.write("error", message, data);
  }

  apiRequest(context: ApiLogContext): number {
    const startedAt = performance.now();
    this.debug(`${context.method} ${context.url} :: request`, {
      payload: sanitize(context.payload),
    });
    return startedAt;
  }

  apiSuccess(context: ApiLogContext, data: unknown, startedAt: number): void {
    this.debug(`${context.method} ${context.url} :: success (${elapsed(startedAt)})`, {
      data: sanitize(data),
    });
  }

  apiFailure(context: ApiLogContext, error: unknown, startedAt: number): void {
    this.error(`${context.method} ${context.url} :: failure (${elapsed(startedAt)})`, sanitize(error));
  }

  private write(level: LogLevel, message: string, data?: unknown): void {
    if (!isApiLoggerEnabled()) return;

    const prefix = `[SAT-014][${this.scope}]`;
    const logger = level === "debug" ? console.debug : console[level];
    if (data === undefined) {
      logger(prefix, message);
      return;
    }
    logger(prefix, message, data);
  }
}

export function createLogger(scope: string): Logger {
  return new Logger(scope);
}

export async function withApiLogging<T>(
  logger: Logger,
  context: ApiLogContext,
  request: () => Promise<T>,
): Promise<T> {
  const startedAt = logger.apiRequest(context);
  try {
    const data = await request();
    logger.apiSuccess(context, data, startedAt);
    return data;
  } catch (error) {
    logger.apiFailure(context, error, startedAt);
    throw error;
  }
}
